<template>
	<v-dialog v-model="localDialog" max-width="500" transition="dialog-transition" content-class="round">
		<v-card class="new_kit pa-3">
			<v-row no-gutters justify="center" v-if="users && localDialog">
				<v-col cols="12" class="text-center my-6">
					<v-form @submit.prevent="handleMainClick">
						<h1 class="big mb-6">{{ title }}</h1>
						<h3 class="light my-4" v-if="showSmallTitle">{{ smallTitle }}</h3>
						<template v-if="showAvatar">
							<InfoAvatar style="display: inline-block" v-for="user in users" :key="user.id" :user="user" :showName="true" />
							<br />
						</template>
						<template v-if="showTextArea">
							<v-textarea
								v-model="emails"
								style="width: 350px; display: inline-block; border-radius: 6px"
								hide-details
								single-line
								outlined
								placeholder="email@email.com, email@email.com"
							/>
							<h4 class="reg mt-6 text-left">Choose a workspace role</h4>
							<RoleSelector :value="localUserRole" :onChange="(role) => (localUserRole = role)" />
							<div class="mb-4" />
						</template>
						<template v-if="showSelect">
							<h4 class="reg mt-6 text-left">Choose a workspace role</h4>
							<RoleSelector :value="localUserRole" :onChange="(role) => (localUserRole = role)" />
							<div class="mb-4" />
						</template>

						<template v-if="showSecondaryButton">
							<v-btn
								class="med_btn mt-10"
								height="40"
								outlined
								tile
								color="#E94E4E"
								@click="secondaryButtonClick"
								:loading="loadingSecondary"
								>{{ secondaryButtonTitle }}</v-btn
							>
							<br />
						</template>
						<v-btn
							:disabled="mainBtnDisabled"
							depressed
							color="primary"
							height="40"
							class="med_btn mt-2"
							type="submit"
							:loading="loading"
							>{{ mainButtonTitle }}</v-btn
						>
					</v-form>
				</v-col>
			</v-row>
		</v-card>
		<CouldNotInviteDialog :isShowDialog.sync="isShowCouldNotInviteDialog" :notInvitedEmails="notInvitedEmails" />
	</v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import CouldNotInviteDialog from './CouldNotInviteDialog';
import RoleSelector from '@/components/ReactComponentsDataProviders/RoleSelector.vue';
import InfoAvatar from '@/components/SettingsManageUsersInfoAvatar';
import { useHasCapability } from '@/store/modules/billing/getters.js';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { ACCOUNT_ROLES, ADMIN_ROLE, ACCOUNT_ROLES_BILLING_FEATURE, AccountRoles } from '@/utils/account-service.const';
export default {
	name: 'SettingsManageUsersDialog',
	components: {
		InfoAvatar,
		CouldNotInviteDialog,
		RoleSelector,
	},
	props: {
		action: { type: String },
		users: { type: Array },
		showDialog: { type: Boolean },
	},
	computed: {
		...mapGetters(['user']),
		...mapGettersShorthand({ useHasCapability }),
		rolesItems() {
			return Object.entries(this.ACCOUNT_ROLES).map(([roleKey, roleText]) => ({
				title: roleText,
				value: roleKey,
				billingFeatureId: ACCOUNT_ROLES_BILLING_FEATURE[roleKey],
			}));
		},
		userIds() {
			return this.users.map((user) => user.id);
		},
		isCurrentUserAdminOfWorkspace() {
			return this.user.profile.account.role === this.ADMIN_ROLE;
		},
		mainBtnDisabled() {
			if (this.action === 'edit_role') {
				return !this.localUserRole;
			}
			return false;
		},
		title() {
			switch (this.action) {
				case 'deactivate_user':
					return 'Are you sure you want to deactivate';
				case 'reactivate':
					return 'Are you sure you want to reactivate';
				case 'send_invitation':
					return 'Add Member';
				case 'edit_role':
					return 'Change Role';
				case 'send_again':
					return 'Re-Invite User';
				case 'revoke':
					return 'Revoke Invitation';

				default:
					return '';
			}
		},
		secondaryButtonTitle() {
			switch (this.action) {
				case 'deactivate_user':
					return 'Deactivate';
				case 'reactivate':
					return 'Reactivate';
				default:
					return '';
			}
		},
		mainButtonTitle() {
			switch (this.action) {
				case 'deactivate_user':
				case 'reactivate':
					return 'Cancel';
				case 'send_invitation':
					return 'Send';
				case 'edit_role':
					return 'Save';
				case 'send_again':
					return 'Invite';
				case 'revoke':
					return 'Revoke';
				default:
					return '';
			}
		},
		showSelect() {
			return this.action === 'edit_role';
		},
		showAvatar() {
			return this.action !== 'send_invitation';
		},
		smallTitle() {
			switch (this.action) {
				case 'send_invitation':
					return "Enter user's emails separated by comma ','";
				default:
					return '';
			}
		},
		showSecondaryButton() {
			return this.action === 'deactivate_user' || this.action === 'reactivate';
		},
		showSmallTitle() {
			return this.action === 'send_invitation';
		},
		showTextArea() {
			return this.action === 'send_invitation';
		},
	},
	methods: {
		...mapActions('account', ['inviteToAccount', 'revokeInvitation', 'updateRoles', 'updateUsersState', 'resendInvitation']),
		async changeRole() {
			this.loading = true;
			try {
				await this.updateRoles({ users: this.userIds, roleCode: this.localUserRole });
				this.localDialog = false;
			} catch {
				this.$toast.error('Something went wrong, please try again later.');
			} finally {
				this.loading = false;
			}
		},
		onSelectRestrictedItem({ e, billingFeatureId, indicator }) {
			e.preventDefault();
			e.stopPropagation();
			indicator.setup().openUpgrade(null, billingFeatureId);
		},
		cancel() {
			this.localDialog = false;
		},
		isRoleOptionDisabled(role) {
			return (role === this.ADMIN_ROLE && !this.isCurrentUserAdminOfWorkspace) || !this.hasCapability(role);
		},
		hasCapability(role) {
			const billingFeatureId = ACCOUNT_ROLES_BILLING_FEATURE[role];
			if (!billingFeatureId) {
				return true;
			}
			return this.useHasCapability(billingFeatureId);
		},
		handleMainClick() {
			switch (this.action) {
				case 'deactivate_user':
				case 'reactivate':
					return this.cancel();
				case 'send_invitation':
					return this.sendMailInvites();
				case 'edit_role':
					return this.changeRole();
				case 'send_again':
					return this.sendUserInvitation();
				case 'revoke':
					return this.revoke();
				default:
					return;
			}
		},
		secondaryButtonClick() {
			if (this.action === 'deactivate_user' || this.action === 'reactivate') {
				return this.toggleUser({ activate: this.action === 'reactivate' });
			}
			if (this.action === 'reactivate') {
				return;
			}
		},
		async toggleUser({ activate }) {
			this.loadingSecondary = true;
			try {
				await this.updateUsersState({ users: this.userIds, activate });
				this.$toast.success(`Users were successfully ${activate ? 'activated' : 'deactivated'}.`);
			} catch (e) {
				console.error(e);
				this.$toast.error('Something went wrong, please try again later.');
			} finally {
				this.loadingSecondary = false;
			}
			this.localDialog = false;
		},
		async sendMailInvites() {
			if (this.emails) {
				const emails = this.emails.split(/[,\n\t\s;:]/).reduce((obj, value) => {
					if (value) {
						const lowerCasedValue = value.toLowerCase();
						!obj.includes(lowerCasedValue) && obj.push(lowerCasedValue);
					}
					return obj;
				}, []);
				this.loading = true;
				let ret;
				try {
					ret = await this.inviteToAccount({ emails, role: this.localUserRole });
				} catch (e) {
					console.error(e);
					this.$toast.error('Something went wrong, please try again later.');
				} finally {
					this.loading = false;
				}
				const emailsOfInvitedUsers = ret.map((r) => r.email);
				this.notInvitedEmails = emails.filter((e) => !emailsOfInvitedUsers.includes(e));
				if (this.notInvitedEmails.length > 0) {
					this.isShowCouldNotInviteDialog = true;
				} else {
					this.$toast.success('Invitations were sent.');
				}
				console.log('send invitation to all mails:', emails, 'saved are', ret);
			}
			this.localDialog = false;
		},
		async sendUserInvitation() {
			this.loading = true;
			const ret = await this.resendInvitation({ uID: this.userIds[0] }).finally(() => {
				this.loading = false;
			});
			if (ret.success) {
				this.$toast.success('Invitation was sent.');
				this.localDialog = false;
			} else {
				this.$toast.error('Invitation was not sent.');
			}
		},
		async revoke() {
			this.loading = true;
			try {
				await this.revokeInvitation({ uID: this.userIds[0] });
				this.$toast.success('Invitation was revoked.');
			} catch (e) {
				console.error(e);
				this.$toast.error('Something went wrong, please try again later.');
			} finally {
				this.loading = false;
			}
			this.localDialog = false;
		},
		resetDialog() {
			this.emails = '';
			this.localUser = null;
			this.localUserRole = AccountRoles.LiteEndUser; // reset to default.
		},
	},
	watch: {
		users: {
			handler(newV) {
				if (newV) {
					if (newV.length === 1) {
						this.localUser = newV[0];
						this.localUserRole = this.localUser.role;
					}
				}
			},
			deep: true,
			immediate: true,
		},
		showDialog: {
			handler(newV) {
				this.localDialog = newV;
				if (!newV) {
					this.resetDialog();
				}
			},
			immediate: true,
		},
		localDialog: {
			handler(newV) {
				this.$emit('update:showDialog', newV);
			},
		},
	},
	data: () => ({
		ACCOUNT_ROLES,
		ADMIN_ROLE,
		manageItems: [
			{ title: 'Active', value: 1 },
			{ title: 'Inactive', value: 2 },
			{ title: 'Invited', value: 3 },
		],
		emails: null,
		localUser: null,
		localDialog: false,
		loading: false,
		loadingSecondary: false,
		isShowCouldNotInviteDialog: false,
		notInvitedEmails: [],
		localUserRole: AccountRoles.LiteEndUser,
	}),
	mounted() {},
};
</script>

<style scoped>
.v-btn.med_btn {
	border-radius: 3px !important;
}
</style>
